import qs from 'query-string';

/**
 * Which public card shop URLs public gallery SSR server-renders: the bare `/cards/wedding/:category`
 * (PRNT-5494), and the crawlable filtered URLs the mega nav prints
 * (`CategoryNav/MegaNavSeoLinks.tsx`) — a single `pf` group whose every option the server already
 * has the data to match, optionally with a `?format=` tile-price display param (PRNT-5582).
 *
 * Read by the server renderer config and by the client gate (`useIsPublicGallerySsr`), which must
 * agree, or the client reproduces a different first render from the server's markup. Kept free of
 * app imports so the server's own bundle can read it; it owns the `pf` parse options for the same
 * reason (`utils/deriveActiveFiltersFromUrl` re-exports them).
 */

/**
 * The browsable gallery categories. Mirrors the keys of
 * `NAMED_CATEGORY_TO_STATIONERY_TEMPLATE_ENUM_MAP` in `Card.routes.tsx` (pinned by the spec);
 * duplicated rather than imported so the server config doesn't pull the router module in.
 */
export const GALLERY_CATEGORY_SLUGS: ReadonlyArray<string> = ['save-the-date', 'invitation', 'thank-you', 'holiday', 'enclosure', 'napkin', 'menu', 'sign'];

/** How `pf` encodes a facet group: `foil=with`, `style=elegant|classic`. */
export const FILTER_QS_PARSE_OPTIONS: qs.ParseOptions = { arrayFormat: 'separator', arrayFormatSeparator: '|' };

/**
 * The facet options the server can match during a render, as `<groupCode>:<valueCode>`.
 *
 * - `foil:with` — `isFoilTemplate`, from the themeId suffix on the summary.
 * - `size:postcard` — `collectSizeFacetTags`, from the summary's width/height.
 * - `photo:with` — the summary's `hasPhoto`, resolved from `themeJSON` by the API.
 * - `style:*` — the theme-search index's concept names, which the server now loads
 *   (`themeSearchIndex.ts`). Ten codes for the nav's nine links: the Elegant link carries
 *   `elegant|classic`, two values of one group. Conditional on the index actually being there —
 *   see `INDEX_BACKED_FACET_GROUPS`.
 *
 * - `format:paper` / `format:digital` / `product:magnet` — the products catalog, which the server
 *   now prefetches into the request's Apollo cache (`stationeryProductsPrefetch.ts`). Both tags are
 *   derived post-`injectProducts` in `useDesignTemplates`, so they exist on the server only once
 *   that cache is seeded — see `PRODUCTS_BACKED_FACET_GROUPS`.
 *
 * Deliberately NOT here: `color:*` is index-backed and would work, but no nav link selects it.
 *
 * `format:digital` is deliberately not category-restricted. The standalone legacy ecards are absent
 * from the summary on every server-rendered gallery URL, the bare category page included, so
 * carving this one option out would hold it to a standard the rest do not meet. The gap is in the
 * summary query (PRNT-5494), not in this list.
 *
 * `price:basic` is excluded for the opposite reason — the summary matches too much. Without the
 * digital designs, `processInitialQueryData` labels every non-premium print template `price:basic`,
 * so a server-rendered Free gallery would list designs that cost money.
 */
export const SERVER_RENDERED_FACET_OPTIONS: ReadonlySet<string> = new Set([
  'foil:with',
  'size:postcard',
  'photo:with',
  'style:botanical',
  'style:floral',
  'style:elegant',
  'style:classic',
  'style:destination',
  'style:typographic',
  'style:rustic',
  'style:minimal',
  'style:vintage',
  'style:whimsical',
  'format:paper',
  'format:digital',
  'product:magnet',
]);

/**
 * Facet groups the server can only match with the theme-search index loaded. A URL selecting one
 * is server-rendered only when the caller passes `hasThemeSearchIndex` — without it the match
 * returns nothing and the page would serve an empty grid rather than fall back to the client.
 *
 * `color` is listed even though no option of it is server-renderable yet, so that adding one
 * cannot accidentally skip the check.
 */
const INDEX_BACKED_FACET_GROUPS: ReadonlySet<string> = new Set(['style', 'color']);

/** The single `pf` group in `search`, or `undefined` when there isn't exactly one. */
const singleFilterGroupCode = (search: string): string | undefined => {
  const productFilters = new URLSearchParams(search).getAll('pf');
  if (productFilters.length !== 1) return undefined;
  const groupCodes = Object.keys(qs.parse(productFilters[0], FILTER_QS_PARSE_OPTIONS));
  return groupCodes.length === 1 ? groupCodes[0] : undefined;
};

/**
 * Whether rendering this URL on the server needs the theme-search index fetched first. Answered
 * from the group code alone, so a hand-typed `?pf=style%3Dnonsense` also reports true and pays for
 * one (cached) index read before the gate rejects it on the unknown option.
 */
export const galleryUrlNeedsThemeSearchIndex = (search: string): boolean => {
  const groupCode = singleFilterGroupCode(search);
  return !!groupCode && INDEX_BACKED_FACET_GROUPS.has(groupCode);
};

/**
 * Facet groups the server can only match with the products catalog in the request's Apollo cache
 * (PRNT-5582): both tags are appended after `injectProducts`, so an empty catalog means an empty
 * grid. `price` is products-adjacent but excluded for a different reason — see
 * `SERVER_RENDERED_FACET_OPTIONS`.
 */
const PRODUCTS_BACKED_FACET_GROUPS: ReadonlySet<string> = new Set(['format', 'product']);

/** Whether this URL needs the catalog prefetched. From the group code alone, as `galleryUrlNeedsThemeSearchIndex` is. */
export const galleryUrlNeedsProducts = (search: string): boolean => {
  const groupCode = singleFilterGroupCode(search);
  return !!groupCode && PRODUCTS_BACKED_FACET_GROUPS.has(groupCode);
};

/**
 * What the renderer has on hand for this request. Each capability is one fact both sides read: the
 * index through the inlined bundle, the catalog through the inlined Apollo cache. That is what
 * keeps the server and client gates from drifting.
 */
export type GalleryServerRenderCapabilities = Readonly<{ hasThemeSearchIndex: boolean; hasProducts: boolean }>;

/** Neither capability assumed: an un-prefetched caller gets today's client render, never an empty grid. */
export const NO_GALLERY_SERVER_RENDER_CAPABILITIES: GalleryServerRenderCapabilities = { hasThemeSearchIndex: false, hasProducts: false };

/**
 * `?format=` only steers which product prices the tiles show; it never filters
 * (`useFormatSearchParam`), so every value the nav emits is server-renderable. Mirrors
 * `GalleryFormatParam`. An unknown value still falls back to the client, so a hand-typed
 * `?format=print` can't produce markup the client won't reproduce.
 */
export const SERVER_RENDERED_FORMAT_PARAMS: ReadonlySet<string> = new Set(['paper', 'premiumDigital', 'all']);

/**
 * Query params that shape the gallery and have no server-rendered form at all. Search pages are
 * not crawlable (phase 3 dropped) and `?theme=` is not a crawlable link, so both keep today's
 * client render. Tracking params (utm_*, gclid, srsltid) are deliberately absent here and from
 * every check below: they don't shape the gallery, so the server renders the same markup however
 * the visitor arrived.
 */
export const CLIENT_ONLY_GALLERY_PARAMS: ReadonlyArray<string> = ['q', 'theme'];

const BARE_GALLERY_PATH = new RegExp(`^/cards/wedding/(${GALLERY_CATEGORY_SLUGS.join('|')})/?$`);

/** True for a public gallery page path, `/cards/wedding/:category` — not a PDP, cart, order or other `/cards` page. */
export const isGalleryPath = (pathname: string): boolean => BARE_GALLERY_PATH.test(pathname);

/**
 * True when `pf` selects exactly one facet group and every option in it is server-renderable.
 * One group because the gallery ANDs groups together and the partial-match tier
 * (`MatchTierDivider`) only appears from two groups up — out of scope here.
 */
export const isServerRenderedFilterParam = (productFiltersParam: string): boolean => {
  const groups = qs.parse(productFiltersParam, FILTER_QS_PARSE_OPTIONS);
  const groupCodes = Object.keys(groups);
  if (groupCodes.length !== 1) return false;

  const groupCode = groupCodes[0];
  // `query-string` yields a string (`foil=with`), `null` for a valueless key (`foil`), or an array
  // of two or more for a separated list (`style=elegant|classic`) — never an empty one, so `every`
  // is never vacuously true here. `null` is rejected by the type check rather than stringified into
  // a composite code that could never match anyway.
  const value = groups[groupCode];
  const valueCodes = Array.isArray(value) ? value : [value];

  return valueCodes.every(valueCode => typeof valueCode === 'string' && SERVER_RENDERED_FACET_OPTIONS.has(`${groupCode}:${valueCode}`));
};

/**
 * True for a URL the server renders the gallery for. The client gate must read this same function
 * with the same capabilities.
 *
 * Both capabilities default to false so a caller that hasn't loaded the data gets today's client
 * render for a URL that needs it rather than an empty grid.
 */
export const isServerRenderedGalleryUrl = (pathname: string, search: string, capabilities: GalleryServerRenderCapabilities = NO_GALLERY_SERVER_RENDER_CAPABILITIES): boolean => {
  if (!isGalleryPath(pathname)) return false;
  if (!capabilities.hasThemeSearchIndex && galleryUrlNeedsThemeSearchIndex(search)) return false;
  if (!capabilities.hasProducts && galleryUrlNeedsProducts(search)) return false;

  const params = new URLSearchParams(search);
  if (CLIENT_ONLY_GALLERY_PARAMS.some(key => params.has(key))) return false;

  // A repeated `pf` or `format` is not a shape the nav emits, and only the first would be read.
  const formats = params.getAll('format');
  if (formats.length > 1 || formats.some(format => !SERVER_RENDERED_FORMAT_PARAMS.has(format))) return false;

  const productFilters = params.getAll('pf');
  if (productFilters.length > 1) return false;
  return productFilters.length === 0 || isServerRenderedFilterParam(productFilters[0]);
};
